import { useEffect, useRef, useCallback } from 'react';
import { getWsUrl, api } from '../api/client';
import type { WSServerMessage, WSClientMessage } from '../types';
import { useAuthStore, useMessagesStore, useRoomsStore, usePresenceStore } from '../store';

interface UseWebSocketOptions {
  roomId: string | null;
  onMessage?: (msg: WSServerMessage) => void;
}

export function useWebSocket({ roomId, onMessage }: UseWebSocketOptions) {
  const wsRef = useRef<{ ws: WebSocket; roomId: string } | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isManualClose = useRef(false);
  const fetchingRoomIdsRef = useRef<Set<string>>(new Set());
  const { token, user } = useAuthStore();
  const { addMessage, } = useMessagesStore();
  const { updateLastMessage, incrementUnread, activeRoomId, upsertRoom } = useRoomsStore();
  const { setOnline, setTyping } = usePresenceStore();

  const activeRoomIdRef = useRef(activeRoomId);
  const userIdRef = useRef(user?.id);

  useEffect(() => {
    activeRoomIdRef.current = activeRoomId;
  }, [activeRoomId]);

  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  const connect = useCallback(() => {
    if (!roomId || !token) return;

    // Cleanup existing
    if (wsRef.current) {
      wsRef.current.ws.close();
    }

    const ws = new WebSocket(getWsUrl(roomId, token));
    wsRef.current = { ws, roomId };

    ws.onopen = () => {
      if (wsRef.current?.ws !== ws) return;
      // Auth message sent via token in URL query param (done in getWsUrl)
      // Still send auth via WS message for DO to map user
      ws.send(JSON.stringify({ type: 'auth', token } as WSClientMessage));

      // Start ping every 30s to keep connection alive
      pingIntervalRef.current = setInterval(() => {
        if (wsRef.current?.ws === ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' } as WSClientMessage));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      if (wsRef.current?.ws !== ws) return;
      try {
        const msg = JSON.parse(event.data as string) as WSServerMessage;

        switch (msg.type) {
          case 'message': {
            const currentRooms = useRoomsStore.getState().rooms;
            const roomExists = currentRooms.some((r) => r.id === msg.data.room_id);
            if (!roomExists) {
              if (fetchingRoomIdsRef.current.has(msg.data.room_id)) {
                addMessage(msg.data.room_id, msg.data);
                break;
              }
              fetchingRoomIdsRef.current.add(msg.data.room_id);
              api.rooms.get(msg.data.room_id).then(({ room }) => {
                fetchingRoomIdsRef.current.delete(msg.data.room_id);
                if (room) {
                  upsertRoom(room);
                  addMessage(msg.data.room_id, msg.data);
                  updateLastMessage(msg.data.room_id, msg.data);
                  if (msg.data.user_id !== userIdRef.current && msg.data.room_id !== activeRoomIdRef.current) {
                    incrementUnread(msg.data.room_id);
                  }
                }
              }).catch(() => {
                fetchingRoomIdsRef.current.delete(msg.data.room_id);
              });
            } else {
              addMessage(msg.data.room_id, msg.data);
              updateLastMessage(msg.data.room_id, msg.data);
              if (msg.data.user_id !== userIdRef.current && msg.data.room_id !== activeRoomIdRef.current) {
                incrementUnread(msg.data.room_id);
              }
            }
            break;
          }
          case 'presence': {
            setOnline(msg.userId, msg.online);
            break;
          }
          case 'typing': {
            setTyping(roomId, msg.userId, msg.userName, msg.isTyping);
            break;
          }
          case 'new_room': {
            upsertRoom(msg.room);
            break;
          }
        }

        onMessage?.(msg);
      } catch {
        // Silence parse errors
      }
    };

    ws.onclose = (event) => {
      if (wsRef.current?.ws !== ws) return;
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);

      // Reconnect after 2s unless manually closed or auth error
      if (!isManualClose.current && event.code !== 4001 && event.code !== 4003) {
        reconnectTimeoutRef.current = setTimeout(() => connect(), 2000);
      }
    };

    ws.onerror = () => {
      // Silence WebSocket errors
    };
  }, [roomId, token]);

  useEffect(() => {
    isManualClose.current = false;
    connect();

    return () => {
      isManualClose.current = true;
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) {
        wsRef.current.ws.close();
      }
    };
  }, [connect]);

  const send = useCallback((msg: WSClientMessage) => {
    if (wsRef.current?.ws.readyState === WebSocket.OPEN) {
      wsRef.current.ws.send(JSON.stringify(msg));
    }
  }, []);

  const sendMessage = useCallback((
    content: string,
    messageType: 'text' | 'image' | 'location' = 'text',
    extra?: { fileUrl?: string; fileName?: string; fileSize?: number }
  ) => {
    send({ type: 'message', content, messageType, ...extra });
  }, [send]);

  const sendTyping = useCallback((isTyping: boolean) => {
    send({ type: 'typing', isTyping });
  }, [send]);

  const sendRead = useCallback((messageId: string) => {
    send({ type: 'read', messageId });
  }, [send]);

  return { send, sendMessage, sendTyping, sendRead };
}
